import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import useTripStore from '../store/useTripStore';
import { toTripState } from '../logic/toTripState';

interface HydrateTripCoreOptions {
  versionId?: string;
  // TO's store is written straight from this hook. DMC opts out and calls
  // `initializeTrip` itself instead (US-048), so it can blank each day's
  // `selectedExtras` first — `toTripState` already fills it from the trip's
  // saved options, and DMC's own hydrate wrapper repopulates it from
  // `initialExtras` through ComposeDayExperience's effect, so loading it
  // unblanked here would add every line twice.
  loadStore?: boolean;
}

interface HydrateTripCoreResult {
  status: 'success';
  trip: any;
  days: any[];
  experiences: Record<string, any>;
  hotels: Record<string, any>;
  initialExtras: any[];
}

/**
 * The part of "open a trip" every app does the same way: fetch it, format it
 * with `toTripState`, and (by default) load it into the shared store. Each
 * app's own `useHydrateTrip.js` wraps this with its own side effects — the
 * client folder, GTM, the " copy" suffix, the rooms composition — none of
 * which belong here since neither is shared (FR-8).
 */
export function useHydrateTripCore() {
  const { api, onEvent } = useTripDesignerConfig();
  const initializeTrip = useTripStore((state) => state.initializeTrip);

  return async (
    tripId: string,
    { versionId, loadStore = true }: HydrateTripCoreOptions = {},
  ): Promise<HydrateTripCoreResult> => {
    const trip: any = (await api.getTrip({ tripId, versionId })).data;
    const { days, experiences, hotels, initialExtras } = await toTripState({
      trip,
      api,
      locale: trip?.locale,
    });

    if (loadStore) {
      initializeTrip({ days, experiences, hotels }, tripId);
    }

    onEvent?.('trip_opened', { tripId, trip, daysCount: days.length });

    return {
      status: 'success',
      trip,
      days,
      experiences,
      hotels,
      initialExtras,
    };
  };
}
